import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useExperiences } from "../../../hooks/useExperiences";
import Footer from "../../structures/Footer";
import "../../../assets/css/booking.css";

export default function BookingPage() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { experiences, loading: expsLoading, getExp } = useExperiences();

  // Memaksimalkan pengambilan data: cek state dulu, kalau kosong cek localStorage
  const [experience, setExperience] = useState(locationState || null);
  const fullExp = getExp(experience?.id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!experience) {
      const saved = localStorage.getItem("selectedExperience");
      if (saved) {
        try {
          setExperience(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to parse saved experience");
        }
      } else {
        // Jika tetap null, redirect ke reservation
        navigate("/reservation", { replace: true });
      }
    }
  }, [experience, navigate]);
  const normalizeTime = (timeValue) => String(timeValue || "").slice(0, 5);
  const timeToMinutes = (timeValue) => {
    const [h, m] = normalizeTime(timeValue).split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return (h * 60) + m;
  };

  const generateTimeRange = (open, close) => {
    if (!open || !close) return [];
    const slots = [];
    let currentMins = timeToMinutes(open);
    const endMins = timeToMinutes(close);

    slots.push(normalizeTime(open));
    let nextMins = (Math.floor(currentMins / 60) + 1) * 60;

    while (nextMins <= endMins) {
      const h = Math.floor(nextMins / 60);
      const m = nextMins % 60;
      const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      if (!slots.includes(timeStr)) slots.push(timeStr);
      nextMins += 60;
    }
    return slots;
  };

  const getExperienceSlots = (exp, dateOverride) => {
    const target = exp || fullExp || experience;
    if (!target) return [];

    // If there's a per-date availability override from the calendar, use it
    const src = dateOverride || target;
    
    // Mantos experiences have fixed hours 10:00-20:00
    const mantosHours = experience?.id?.startsWith('mantos-');
    const openT1 = mantosHours ? "10:00" : (src.openTime || target.openTime || "08:00");
    const closeT1 = mantosHours ? "20:00" : (src.closeTime || target.closeTime || "20:00");
    const openT2 = src.openTime2 || target.openTime2;
    const closeT2 = src.closeTime2 || target.closeTime2;

    const slots1 = generateTimeRange(openT1, closeT1);
    const slots2 = generateTimeRange(openT2, closeT2);

    const combined = [...slots1, ...slots2];
    return combined.length > 0 ? combined : generateTimeRange("08:00", "20:00");
  };

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    pax: 1,
    date: "",
    time: "",
    specialRequests: "",
    withDrone: false,
    promoCode: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [bookedPaxByDate, setBookedPaxByDate] = useState({});
  const [bookedPaxByDateTime, setBookedPaxByDateTime] = useState({});
  const [bookedStartTimes, setBookedStartTimes] = useState({}); // tracks actual booking start times: { "YYYY-MM-DD|HH:MM": true }
  const [activeBookings, setActiveBookings] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]); // rules for primary experience
  const [allAvailabilityRules, setAllAvailabilityRules] = useState([]); // rules for ALL experiences (for addon closed check)
  const [notification, setNotification] = useState({ show: false, title: "", message: "", type: "success" });
  
  // 🔥 New state for Multiple Experiences — { addonId: paxCount }
  const [selectedAddons, setSelectedAddons] = useState({});

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => {
      const next = { ...prev };
      if (next[addonId]) {
        delete next[addonId];
      } else {
        next[addonId] = 1;
      }
      return next;
    });
  };

  const setAddonPax = (addonId, newPax) => {
    setSelectedAddons(prev => ({
      ...prev,
      [addonId]: Math.max(1, newPax)
    }));
  };


  const getBookingLimit = (targetDate) => {
    const block = blockedDates.find(b => b.date === targetDate);
    const expDefault = fullExp?.defaultCapacity || 8;
    return block && block.slots && block.slots.length > 0 && block.slots[0].maxPax ? block.slots[0].maxPax : expDefault;
  };

  const getRemainingCapacity = (targetDate, targetTime, customExpId) => {
    const targetId = customExpId || experience?.id;
    const targetFullExp = customExpId 
      ? (experiences || []).find(e => e.id === customExpId) 
      : (fullExp || experience);

    const singleGroupExperiences = ['sailing', 'prewedding'];

    // Find custom availability rule from calendar/admin
    const customRule = allAvailabilityRules.find(r => r.experienceId === targetId && r.date === targetDate);

    // If date is closed by admin globally
    if (customRule && customRule.isClosed) {
      return 0;
    }

    if (!targetId || !targetFullExp) {
      // Mantos experiences have 6 pax limit
      if (targetId?.startsWith('mantos-')) return 6;
      return 6; // default fallback
    }
    if (!targetTime) {
      if (singleGroupExperiences.includes(targetId)) {
        return targetFullExp?.defaultCapacity || 15;
      }
      const isSessionBased = sessionBasedExperiences.includes(targetId);
      if (targetId?.startsWith('mantos-')) return targetFullExp?.defaultCapacity || 6;
      return isSessionBased
        ? (maxSessionsMap[targetId] || 1)
        : (targetFullExp?.defaultCapacity || 6);
    }

    const isSessionBased = sessionBasedExperiences.includes(targetId);
    const isSingleGroup = singleGroupExperiences.includes(targetId);

    let baseLimit = isSessionBased
      ? (maxSessionsMap[targetId] || 1)
      : (targetFullExp?.defaultCapacity || 6);

    // Mantos experiences have 6 pax limit per session
    if (targetId?.startsWith('mantos-')) {
      baseLimit = targetFullExp?.defaultCapacity || 6;
    }

    // Check if slot has override in customRule
    const slotOverride = customRule?.slots?.find(s => s.time === targetTime);
    if (slotOverride) {
      if (slotOverride.isOpen === false) {
        return 0; // Slot is closed
      }
      if (!isSingleGroup && typeof slotOverride.maxPax === 'number') {
        baseLimit = slotOverride.maxPax;
      }
    }

    // Check all overlapping slots for the selected time
    const startMins = timeToMinutes(targetTime);
    let durationMins = 60;
    const dur = (targetFullExp?.duration || '').toUpperCase();
    if (dur.includes('3 JAM')) durationMins = 180;
    else if (dur.includes('2.5 JAM') || dur.includes('1-2.5 JAM')) durationMins = 150;
    else if (dur.includes('2 JAM')) durationMins = 120;
    else if (dur.includes('1.5 JAM')) durationMins = 90;
    else if (dur.includes('1 JAM')) durationMins = 60;
    else if (dur.includes('30 MENIT')) durationMins = 30;

    const endMins = startMins + durationMins;
    let maxOccupied = 0;

    const allSlotsList = [
      "05:00", "05:30", "06:00", "06:30", "07:00", "07:30", "08:00", "08:30",
      "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
      "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
      "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30"
    ];

    allSlotsList.forEach(s => {
      const sStart = timeToMinutes(s);
      if (sStart >= startMins && sStart < endMins) {
        const occupied = bookedPaxByDateTime[`${targetDate}|${s}`] || 0;
        maxOccupied = Math.max(maxOccupied, occupied);
      }
    });

    const remainingSessions = Math.max(0, baseLimit - maxOccupied);
    if (isSingleGroup) {
      if (remainingSessions <= 0) return 0;
      if (slotOverride && typeof slotOverride.maxPax === 'number') {
        return slotOverride.maxPax;
      }
      return targetFullExp?.defaultCapacity || 15;
    }
    return remainingSessions;
  };

  // Auto-verify addon capacities when date/time changes
  useEffect(() => {
    if (form.date && form.time && Object.keys(selectedAddons).length > 0) {
      setSelectedAddons(prev => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach(addonId => {
          const cap = getRemainingCapacity(form.date, form.time, addonId);
          if (cap <= 0) {
            delete next[addonId];
            changed = true;
          } else if (next[addonId] > cap) {
            next[addonId] = cap;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [form.date, form.time, experiences]);

  const sessionBasedExperiences = ['sailing', 'kayak', 'prewedding'];

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Workshop experiences: customers must join existing sessions (mid-session blocking)
  // Uses partial matching: 'pottery' matches 'pottery', 'pottery-stoneware', etc.
  const workshopKeywords = ['pottery', 'painting', 'paint-on-pottery', 'combo'];
  const isWorkshopExperience = workshopKeywords.some(w => experience?.id?.includes(w));

  // Check if this is a mantos experience (special event with 6 pax limit)
  const isMantosExperience = experience?.id?.startsWith('mantos-');

  const maxSessionsMap = {
    kayak: 2,
    sailing: 1,
    prewedding: 1,
  };

  const sessionUnitMap = {
    kayak: "Boat",
    sailing: "Trip",
    pottery: "Pax",
    "paint-on-pottery": "Pax",
    combo: "Pax",
    bicycle: "Pax",
    mantos: "Pax",
    // default is "Slot"
  };

  const API_BASE = process.env.REACT_APP_BACKEND_API || '';

  // Calculate total price whenever pax changes
  useEffect(() => {
    const currentPrice = experience?.price || fullExp?.price || 0;
    const minLimit = fullExp?.minPax || 1;

    // Auto-adjust pax if below minimum
    if (form.pax < minLimit) {
      setForm(prev => ({ ...prev, pax: minLimit }));
    }

    if (form.pax) {
      let baseTotal = 0;
      if (experience?.id === 'prewedding') {
        baseTotal = currentPrice;
      } else if (experience?.id === 'sailing') {
        const effectivePaxForPrice = Math.max(parseInt(form.pax), 5);
        baseTotal = currentPrice * effectivePaxForPrice;
      } else if (experience?.id === 'bicycle') {
        const minBicyclePax = fullExp?.minPax || 3;
        const effectivePaxForPrice = Math.max(parseInt(form.pax), minBicyclePax);
        baseTotal = currentPrice * effectivePaxForPrice;
        if (form.withDrone) {
          baseTotal += 300000;
        }
      } else if (experience?.id === 'kayak') {
        baseTotal = currentPrice;
      } else {
        baseTotal = currentPrice * parseInt(form.pax);
        // withDrone sudah ditangani di block bicycle di atas
      }

      // 🔥 Calculate Addons Price (per-addon pax)
      const addonIds = Object.keys(selectedAddons);
      if (addonIds.length > 0 && experiences) {
        addonIds.forEach(addonId => {
          const addon = experiences.find(e => e.id === addonId);
          if (addon) {
            let addonPax = selectedAddons[addonId] || 1;
            if (addonId === 'sailing') addonPax = Math.max(addonPax, 5);
            if (addonId === 'bicycle') addonPax = Math.max(addonPax, addon.minPax || 3);
            if (addonId === 'kayak') {
              // Addon kayak logic: price is per boat
              baseTotal += addon.price * addonPax;
            } else if (addonId === 'prewedding') {
              baseTotal += addon.price;
            } else {
              baseTotal += addon.price * addonPax;
            }
          }
        });
      }

      // Re-calculate discount if promo is applied
      let discAmount = 0;
      if (appliedPromo) {
        if (appliedPromo.type === 'percentage') {
          discAmount = Math.round(baseTotal * (appliedPromo.value / 100));
        } else if (appliedPromo.type === 'fixed') {
          discAmount = appliedPromo.value;
        }
      }
      setDiscount(discAmount);
      setTotalPrice(Math.max(0, baseTotal - discAmount));
    }
  }, [form.pax, experience, fullExp, form.withDrone, appliedPromo, selectedAddons, experiences]);

  // Lock scroll when modal is open
  useEffect(() => {
    if (notification.show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [notification.show]);

  // Scroll animation
  useEffect(() => {
    const animateElements = document.querySelectorAll('.animate-on-scroll');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, { threshold: 0.1 });

    animateElements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, experience]);

  // Load booking density and blocked dates
  useEffect(() => {
    if (!experience) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const expId = experience.id;

        let currentFullExp = null;
        // 0. Load all experiences to get full details (openTime, etc)
        const resE = await fetch(`${API_BASE}/api/experiences`);
        const jsonE = await resE.json();
        if (jsonE.success && Array.isArray(jsonE.data)) {
          currentFullExp = jsonE.data.find(e => e.id === expId);
        }

        // 1. Load existing bookings
        const resB = await fetch(`${API_BASE}/api/bookings`, {
          headers: { "ngrok-skip-browser-warning": "true" }
        });
        const jsonB = await resB.json();

        // 2. Load blocked/custom availability for primary experience
        const resA = await fetch(`${API_BASE}/api/availability?experienceId=${expId}`, {
          headers: { "ngrok-skip-browser-warning": "true" }
        });
        const jsonA = await resA.json();

        // 3. Load availability rules for ALL experiences (to know which addons are closed)
        const resAllA = await fetch(`${API_BASE}/api/availability`, {
          headers: { "ngrok-skip-browser-warning": "true" }
        });
        const jsonAllA = await resAllA.json();
        if (jsonAllA.success && Array.isArray(jsonAllA.data)) {
          setAllAvailabilityRules(jsonAllA.data);
        }

        if (jsonB.success && Array.isArray(jsonB.data)) {
          const counts = jsonB.data.reduce((acc, booking) => {
            if (!booking?.date) return acc;
            if (booking.status === "FAILED" || booking.status === "CANCELLED") return acc;

            const bExpId = booking.experience || (booking.experienceDetails && booking.experienceDetails.id) || null;
            // Filter: only count if it's the same experience (bicycle & bicycle-drone are treated as same)
            const isBicycle = expId?.startsWith('bicycle') && bExpId?.startsWith('bicycle');
            const potteryIds = ['pottery', 'paint-on-pottery', 'combo'];
            const isPottery = potteryIds.some(p => expId?.includes(p)) && potteryIds.some(p => bExpId?.includes(p));
            const isMantos = expId?.startsWith('mantos-') && bExpId?.startsWith('mantos-');
            const isMatch = bExpId === expId || isBicycle || isPottery || isMantos;

            if (!isMatch) return acc;

            const pax = Number(booking.pax) || 1;
            acc[booking.date] = (acc[booking.date] || 0) + pax;
            return acc;
          }, {});

          const allSlotsList = getExperienceSlots(currentFullExp || experience);

          const slotCounts = {};
          const startTimes = {}; // Track actual booking start times
          jsonB.data.forEach(booking => {
            if (!booking?.date || !booking?.time) return;
            if (booking.status === "FAILED" || booking.status === "CANCELLED") return;

            const bExpId = booking.experience || (booking.experienceDetails && booking.experienceDetails.id) || null;
            const isBicycle = expId?.startsWith('bicycle') && bExpId?.startsWith('bicycle');
            const potteryIds = ['pottery', 'paint-on-pottery', 'combo'];
            const isPottery = potteryIds.some(p => expId?.includes(p)) && potteryIds.some(p => bExpId?.includes(p));
            const isMantos = expId?.startsWith('mantos-') && bExpId?.startsWith('mantos-');
            const isMatch = bExpId === expId || isBicycle || isPottery || isMantos;

            if (!isMatch) return;

            const pax = Number(booking.pax) || 1;
            const bStartTime = normalizeTime(booking.time);
            const bStartMins = timeToMinutes(bStartTime);

            // Mark the actual start time of this booking
            const startKey = `${booking.date}|${bStartTime}`;
            startTimes[startKey] = true;

            let durationMins = 0;
            const bExpsList = (booking.experiences && booking.experiences.length > 0) ? booking.experiences : [bExpId];
            
            bExpsList.forEach(exId => {
              const exObj = jsonE.data.find(e => e.id === exId) || {};
              const durStr = (exObj.duration || (booking.experienceDetails && booking.experienceDetails.duration) || '').toUpperCase();
              
              if (durStr.includes('3 JAM')) durationMins += 180;
              else if (durStr.includes('2.5 JAM') || durStr.includes('1-2.5 JAM')) durationMins += 150;
              else if (durStr.includes('2 JAM')) durationMins += 120;
              else if (durStr.includes('1.5 JAM')) durationMins += 90;
              else if (durStr.includes('1 JAM')) durationMins += 60;
              else if (durStr.includes('30 MENIT')) durationMins += 30;
              else durationMins += 60; // fallback per activity
            });

            if (durationMins === 0) durationMins = 60;

            const bEndMins = bStartMins + durationMins;

            allSlotsList.forEach(slot => {
              const slotStart = timeToMinutes(slot);
              // Mark slot as occupied only if its start time falls within the booking duration
              if (slotStart >= bStartMins && slotStart < bEndMins) {
                const slotKey = `${booking.date}|${slot}`;
                
                const isSessionBased = sessionBasedExperiences.includes(expId);
                const increment = isSessionBased ? 1 : (Number(booking.pax) || 1);
                
                slotCounts[slotKey] = (slotCounts[slotKey] || 0) + increment;
              }
            });
          });

          setBookedPaxByDate(counts);
          setBookedPaxByDateTime(slotCounts);
          setBookedStartTimes(startTimes);
        }

        if (jsonA.success && Array.isArray(jsonA.data)) {
          setBlockedDates(jsonA.data);
        }
      } catch (err) {
        console.warn("Failed to load availability data", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [API_BASE, experience]);

  const formatDateKey = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = `${dateObj.getMonth() + 1}`.padStart(2, "0");
    const d = `${dateObj.getDate()}`.padStart(2, "0");
    return `${y}-${m}-${d}`;
  };



  const monthLabel = new Intl.DateTimeFormat("id-ID", {
    month: "long",
    year: "numeric",
  }).format(calendarMonth);

  const weekdayLabels = ["MIN", "SEN", "SEL", "RAB", "KAM", "JUM", "SAB"];

  const today = new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Makassar' }) + "T00:00:00");
  // today.setHours(0, 0, 0, 0); // Sudah diset ke midnight oleh string T00:00:00

  const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
  const daysInMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
  const startWeekday = firstOfMonth.getDay();

const calendarCells = [];
    for (let i = 0; i < startWeekday; i += 1) {
      calendarCells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      calendarCells.push(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
    }

    const goMonth = (delta) => {
      setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

  const handleDateSelect = (dateObj) => {
    const key = formatDateKey(dateObj);
    const isBeforeToday = dateObj < today;
    const isToday = dateObj.getTime() === today.getTime();

    // For mantos: restrict to July 1-5, 2026 only
    if (isMantosExperience) {
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth();
      const day = dateObj.getDate();
      const isJuly2026 = year === 2026 && month === 6; // Month 6 = July (0-indexed)
      const isInRange = day >= 1 && day <= 5;
      if (!isJuly2026 || !isInRange) {
        return; // Ignore clicks outside valid range
      }
    }

    // Check if date is blocked by admin
    const block = blockedDates.find(b => b.date === key);
    
    // Default: Izinkan booking hari ini (Hari H), batasi hanya tanggal sebelum hari ini
    const isPast = isBeforeToday;
    const isBlocked = block && block.isClosed;

    const allSlotsList = getExperienceSlots(fullExp || experience, block);
    const isSessionBased = sessionBasedExperiences.includes(experience?.id);
    const capacity = isSessionBased
      ? (maxSessionsMap[experience?.id] || 1)
      : (fullExp?.defaultCapacity || 6);
    let availableCount = 0;
    allSlotsList.forEach(slot => {
      const isSlotBlocked = block && block.slots && block.slots.includes(slot);
      if (!isSlotBlocked) {
        const slotBookedCount = bookedPaxByDateTime[`${key}|${slot}`] || 0;
        if (slotBookedCount < capacity) availableCount++;
      }
    });

    const isFull = isBlocked || availableCount === 0;

    if (isPast || isFull) return;

    setForm((prev) => {
      const currentSlotKey = `${key}|${prev.time}`;
      const currentSlotBooked = bookedPaxByDateTime[currentSlotKey] || 0;

      // Also check if this specific slot is blocked
      const isSlotBlocked = block && block.slots && block.slots.includes(prev.time);

      const shouldResetTime = prev.time && (currentSlotBooked >= capacity || isSlotBlocked);
      const selectedTime = shouldResetTime ? "" : prev.time;

      const dynamicLimit = experience?.id === 'prewedding' ? 1 : getRemainingCapacity(key, selectedTime);
      const newPax = Math.min(prev.pax, dynamicLimit);

      return { ...prev, date: key, time: selectedTime, pax: newPax };
    });
    if (errors.date) {
      setErrors((prev) => ({ ...prev, date: "" }));
    }
  };

  const handleTimeSelect = (slotTime) => {
    if (!form.date) return;

    const remaining = getRemainingCapacity(form.date, slotTime);
    if (remaining <= 0) return;

    const dynamicLimit = experience?.id === 'prewedding' ? 1 : remaining;
    
    setForm((prev) => ({ 
      ...prev, 
      time: slotTime,
      pax: Math.min(prev.pax, dynamicLimit)
    }));
    setErrors((prev) => ({ ...prev, time: "", date: "" }));
  };

  const handleApplyPromo = async () => {
    const code = form.promoCode.toUpperCase().trim();
    if (!code) {
      setAppliedPromo(null);
      setDiscount(0);
      return;
    }

    try {
      const API_BASE = (process.env.REACT_APP_BACKEND_API || "http://localhost:5000").replace(/\/$/, "");
      const res = await fetch(`${API_BASE}/api/promos/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          experienceId: experience?.id
        })
      });

      const data = await res.json();

      if (res.ok && data.valid) {
        setAppliedPromo(data.promo);
        // Success - UI will update via useEffect since appliedPromo changed
      } else {
        setNotification({
          show: true,
          title: "Invalid Code",
          message: data.message || "The promo code you entered is invalid.",
          type: "error"
        });
        setAppliedPromo(null);
        setDiscount(0);
      }
    } catch (err) {
      setNotification({
        show: true,
        title: "Connection Error",
        message: "Failed to connect to promo server.",
        type: "error"
      });
    }
  };

  // If no experience selected or still loading initial data
  if (loading || !experience) {
    return (
      <div className="booking-loading-overlay">
        <div className="booking-loading-spinner"></div>
        <div className="booking-loading-text">Preparing your experience...</div>
      </div>
    );
  }


  // Get per-date override if a date is selected
  const selectedBlock = form.date ? blockedDates.find(b => b.date === form.date) : null;
  const slots = getExperienceSlots(fullExp || experience, selectedBlock);
  const timeSlotSections = [
    { label: "AVAILABLE SESSIONS", slots: slots }
  ];



  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Full name is required";
    } else if (form.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s+()-]+$/.test(form.phone)) {
      newErrors.phone = "Phone number is invalid";
    }

    if (!form.date) {
      newErrors.date = "Date is required";
    } else {
      const selectedDate = new Date(form.date + "T00:00:00");
      const today = new Date(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Makassar' }) + "T00:00:00");
      // today.setHours(0, 0, 0, 0);

      const block = blockedDates.find(b => b.date === form.date);
      const isBeforeToday = selectedDate < today;
      const isToday = selectedDate.getTime() === today.getTime();
      const isForceOpen = block && block.isForceOpen;

      if (isBeforeToday) {
        newErrors.date = "Bookings cannot be made for past dates";
      } else {
        const isBlocked = block && block.isClosed;
        const allSlotsList = [
          "05:00", "05:30", "06:00", "06:30", "07:00", "07:30", "08:00", "08:30",
          "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
          "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
          "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30"
        ];
        let availableCount = 0;
        const isSessionBased = sessionBasedExperiences.includes(experience?.id);
        const capacity = isSessionBased
          ? (maxSessionsMap[experience?.id] || 1)
          : (fullExp?.defaultCapacity || 6);
        allSlotsList.forEach(slot => {
          const isSlotBlocked = block && block.slots && block.slots.includes(slot);
          if (!isSlotBlocked) {
            const slotOccupied = bookedPaxByDateTime[`${form.date}|${slot}`] || 0;
            if (slotOccupied < capacity) availableCount++;
          }
        });
        if (isBlocked || availableCount === 0) {
          newErrors.date = "Selected date is fully booked";
        }
      }
    }

    if (!form.time) {
      newErrors.time = "Time is required";
    } else {
      const remaining = getRemainingCapacity(form.date, form.time);
      if (remaining <= 0) {
        newErrors.time = "Selected time slot is no longer available";
      }
    }

    const minLimit = fullExp?.minPax || 1;
    if (form.pax < minLimit) {
      newErrors.pax = `At least ${minLimit} person${minLimit > 1 ? 's' : ''} required`;
    } else {
      const maxLimit = getRemainingCapacity(form.date, form.time);
      if (form.pax > maxLimit) {
        newErrors.pax = `Only ${maxLimit} spot${maxLimit > 1 ? 's' : ''} remaining for this time slot`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Form submission including API call
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const selectedBlock = blockedDates.find(b => b.date === form.date);
    const isForceOpen = selectedBlock && selectedBlock.isForceOpen;

    try {
      const res = await fetch(`${API_BASE}/api/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true"
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          experience: experience.id,
          experiences: [experience.id, ...Object.keys(selectedAddons)],
          addonPax: selectedAddons,
          experienceName: experience?.name || fullExp?.name || "Bicycle Rental",
          pax: form.pax,
          originalPrice: totalPrice + discount,
          discount: discount,
          totalPrice: totalPrice,
          date: form.date,
          time: form.time,
          withDrone: form.withDrone,
          promoCode: form.promoCode,
          skipH1Check: isForceOpen, // Izinkan booking hari-H jika di-Force Open
          experienceDetails: {
            name: (experience?.id === 'bicycle' && form.withDrone) ? "Bicycle Rental + Drone Content" : (experience?.name || fullExp?.name || "Bicycle Rental"),
            price: experience?.price || fullExp?.price || 175000,
            emoji: experience?.icon || fullExp?.emoji || "🚴"
          },
          frontendUrl: window.location.origin // MENGIRIM ASAL URL
        }),
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to create booking");
      }

      const savedBooking = data.data;

      const addonsList = Object.keys(selectedAddons).map(addonId => {
        const addon = (experiences || []).find(e => e.id === addonId);
        return {
          id: addonId,
          name: addon?.name || addonId,
          price: addon?.price || 0,
          emoji: addon?.emoji || '',
          pax: selectedAddons[addonId] || 1
        };
      });

      // 🔴 DATA YANG AKAN DIPAKAI DI PAYMENT PAGE
      const bookingData = {
        bookingId: savedBooking.bookingId,
        name: savedBooking.name,
        email: savedBooking.email,
        phone: savedBooking.phone,
        pax: savedBooking.pax,
        date: savedBooking.date,
        time: savedBooking.time,
        originalPrice: savedBooking.originalPrice || (totalPrice + discount),
        discount: savedBooking.discount || discount,
        totalPrice: savedBooking.totalPrice,
        withDrone: savedBooking.withDrone,
        experience: {
          ...experience,
          name: (experience?.id === 'bicycle' && savedBooking.withDrone) ? "Bicycle Rental + Drone Content" : (fullExp?.name || experience.name),
          price: fullExp?.price || experience.price,
          duration: fullExp?.duration || experience.duration,
          icon: fullExp?.emoji || experience.icon,
        },         // objek experience dari frontend
        experienceName: (experience?.id === 'bicycle' && savedBooking.withDrone) ? "Bicycle Rental + Drone Content" : (fullExp?.name || experience.name || ''), // cadangan nama pengalaman sebagai string
        addons: addonsList,
        status: savedBooking.status,    // PENDING_PAYMENT dll kalau ada
      };

      // 🔥 Simpan booking baru
      localStorage.setItem("bookingData", JSON.stringify(bookingData));

      // 🔥 Hapus Snap Token lama → supaya Midtrans generate token baru
      localStorage.removeItem("snapToken");

      // Redirect ke halaman payment (dinamis berdasarkan experience id)
      const isMantos = experience?.id?.startsWith('mantos-');
      navigate(isMantos ? "/mantos/booking/payment" : "/booking/payment");

    } catch (err) {
      console.error("Error:", err);
      alert(err.message || "Gagal membuat booking");
    } finally {
      setIsSubmitting(false);
    }
  };


  const primaryFullExp = fullExp || experience || {};
  const allSelectedExps = [
    primaryFullExp,
    ...(Object.keys(selectedAddons) || []).map(id => (experiences || []).find(e => e.id === id)).filter(Boolean)
  ];

  return (
    <div className="booking-page-container">
      {/* Header Section */}
      <section className="booking-header-section animate-on-scroll">
        <div className="booking-header-content">
          <div className="header-ornament">◆</div>
          <h1 className="booking-main-title">COMPLETE YOUR BOOKING</h1>
          <p className="booking-subtitle">
            You're one step away from an unforgettable experience at À L'AISE
          </p>
        </div>
      </section>

      {/* Main Booking Content */}
      <section className="booking-content-section">
        <div className="booking-content-wrapper">

          {/* Left Side - Experience Summary */}
          <div className="booking-summary animate-on-scroll">
            <div className="summary-header">
              <h2>Experience Summary</h2>
              <div className="summary-divider"></div>
            </div>

            <div className="experience-card">
              {allSelectedExps.map((exp, idx) => {
                const isPrimary = idx === 0;
                const isKayak = exp.id === 'kayak';
                const isPrewed = exp.id === 'prewedding';
                const isBicycle = exp.id === 'bicycle';
                const bicycleMinPax = fullExp?.minPax || 3;
                const priceLabel = isKayak ? 'Price per Boat' : isPrewed ? 'Package Price' : 'Price per Person';
                const priceValue = exp.price || 0;
                const dur = exp.duration || "2 JAM";
                
                return (
                  <div key={exp.id || idx} style={{ marginBottom: idx < allSelectedExps.length - 1 ? '16px' : '0', paddingBottom: idx < allSelectedExps.length - 1 ? '16px' : '0', borderBottom: idx < allSelectedExps.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                    <div className="experience-card-header">
                      <h3>{exp.name}</h3>
                    </div>

                    <div className="experience-details">
                      <div className="detail-item">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{dur}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{priceLabel}</span>
                        <span className="detail-value">IDR {priceValue.toLocaleString()}</span>
                      </div>
                      {/* Only show guest count once at the bottom of the first primary experience */}
                      {isPrimary ? (
                        <div className="detail-item">
                          <span className="detail-label">Number of Guests</span>
                          <div className="detail-value-group">
                            <span className="detail-value">{form.pax} {form.pax > 1 ? 'people' : 'person'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-item">
                          <span className="detail-label">Qty</span>
                          <div className="detail-value-group">
                            <span className="detail-value">{selectedAddons[exp.id] || 1} {exp.id === 'kayak' ? (selectedAddons[exp.id] > 1 ? 'boats' : 'boat') : (selectedAddons[exp.id] > 1 ? 'pax' : 'pax')}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Minimum charge notice for Bicycle — ditampilkan di dalam card setelah guest count */}
                    {isPrimary && isBicycle && form.pax < bicycleMinPax && (
                      <div style={{
                        margin: '10px 0 0 0',
                        padding: '9px 13px',
                        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                        border: '1px solid #f59e0b',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        fontSize: '12px',
                        color: '#92400e',
                        lineHeight: '1.5'
                      }}>
                        <div>
                          <strong style={{ display: 'block', marginBottom: '2px' }}>Harga dihitung untuk {bicycleMinPax} orang</strong>
                          Booking sepeda dikenakan biaya setara {bicycleMinPax} orang, meskipun jumlah peserta kurang dari {bicycleMinPax}.
                        </div>
                      </div>
                    )}
                    
                    {/* Show notes for each experience if available */}
                    {(exp.notes || exp.includes?.length > 0 || exp.description) ? (
                      <div className="experience-notes-section" style={{ marginTop: '16px' }}>
                        {isPrimary && <h4 className="notes-heading">Experience Details</h4>}
                        {exp.description && (
                          <p className="experience-description-text">{exp.description}</p>
                        )}
                        {exp.notes ? (
                          Array.isArray(exp.notes) ? (
                            <ul className="notes-list">
                              {exp.notes.map((note, nIdx) => (
                                <li key={nIdx}>
                                  <span className="bullet">•</span>
                                  <span>{note}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="note-text">{exp.notes}</p>
                          )
                        ) : (
                          exp.includes?.length > 0 && (
                            <ul className="notes-list">
                              {exp.includes.map((note, nIdx) => (
                                <li key={nIdx}>
                                  <span className="bullet">•</span>
                                  <span>{note}</span>
                                </li>
                              ))}
                            </ul>
                          )
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}

{/* Additional Activities Section - Model 1 Editorial Line-Items */}
               {experiences && experiences.filter(e => e.id !== experience?.id && !e.name.includes('Legacy') && !e.id?.startsWith('mantos-') && !experience?.id?.startsWith('mantos-')).length > 0 && (
                <div className="experience-addons-section" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <h4 style={{ fontFamily: '"Outfit", sans-serif', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px', color: '#18181b', fontWeight: 600, margin: 0 }}>
                      Enhance Your Visit
                    </h4>
                    <span style={{ fontFamily: '"Outfit", sans-serif', fontSize: '11px', color: '#8B7355', fontWeight: 500, letterSpacing: '0.5px' }}>
                      Optional
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    border: '1px solid #ede8e1', 
                    borderRadius: '14px', 
                    background: '#ffffff', 
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                  }}>
                    {experiences.filter(e => e.id !== experience?.id && !e.name.includes('Legacy') && !e.id?.startsWith('mantos-')).map((addon, aIdx, arr) => {
                      const isSelected = !!selectedAddons[addon.id];
                      const addonPaxCount = selectedAddons[addon.id] || 1;
                      const unitLabel = addon.id === 'kayak' ? 'boat' : 'pax';
                      
                      // Calculate dynamic capacity if date & time are selected
                      const hasDateTime = !!(form.date && form.time);
                      const maxCap = hasDateTime ? getRemainingCapacity(form.date, form.time, addon.id) : 99;
                      const isFull = hasDateTime && maxCap <= 0;
                      const isPrewed = addon.id === 'prewedding';

                      // Check if this addon is closed on the selected date (via admin availability rules) or inactive globally
                      const isInactive = addon.isActive === false;
                      const isClosedByAdmin = isInactive || (form.date
                        ? allAvailabilityRules.some(r => r.experienceId === addon.id && r.date === form.date && r.isClosed)
                        : false);

                      return (
                        <div 
                          key={addon.id} 
                          style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            borderBottom: aIdx < arr.length - 1 ? '1px solid #f1f0ea' : 'none',
                            borderLeft: isSelected ? '3px solid #8B7355' : '3px solid transparent',
                            background: isClosedByAdmin ? '#fafafa' : isSelected ? '#FAF8F5' : '#ffffff', 
                            transition: 'all 0.2s ease', 
                            opacity: (isFull || isClosedByAdmin) ? 0.45 : 1,
                            pointerEvents: (isFull || isClosedByAdmin) ? 'none' : 'auto'
                          }}
                        >
                          <div 
                            onClick={() => {
                              if (!isFull && !isClosedByAdmin) toggleAddon(addon.id);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '13px 16px',
                              cursor: (isFull || isClosedByAdmin) ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingRight: '8px' }}>
                              <span style={{ 
                                fontFamily: '"Outfit", sans-serif',
                                fontSize: '13.5px', 
                                fontWeight: isSelected ? 600 : 500, 
                                color: isSelected ? '#18181b' : '#334155', 
                                lineHeight: '1.3' 
                              }}>
                                {addon.name}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ 
                                  fontFamily: '"Outfit", sans-serif',
                                  fontSize: '12px', 
                                  color: isSelected ? '#8B7355' : '#71717a', 
                                  fontWeight: 500 
                                }}>
                                  + IDR {addon.price.toLocaleString()} / {unitLabel}
                                </span>
                                {isClosedByAdmin && (
                                  <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                                    (Tidak tersedia)
                                  </span>
                                )}
                                {!isClosedByAdmin && isFull && (
                                  <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                                    (Penuh pada {form.time})
                                  </span>
                                )}
                              </div>
                            </div>

                            <div style={{ 
                              width: '22px', 
                              height: '22px', 
                              borderRadius: '50%', 
                              border: isSelected ? '1.5px solid #8B7355' : '1.5px solid #d4d4d8',
                              background: isSelected ? '#8B7355' : '#ffffff',
                              color: isSelected ? '#ffffff' : '#71717a',
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: isSelected ? '11px' : '13px',
                              fontWeight: isSelected ? 'bold' : '500',
                              flexShrink: 0,
                              transition: 'all 0.2s ease'
                            }}>
                              {isSelected ? '✓' : '+'}
                            </div>
                          </div>
                          
                          {/* Per-addon pax control */}
                          {isSelected && !isPrewed && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between', 
                              padding: '8px 16px 12px', 
                              borderTop: '1px dashed #e7e2d9',
                              background: '#FAF8F5'
                            }}>
                              <span style={{ fontFamily: '"Outfit", sans-serif', fontSize: '12px', color: '#71717a', fontWeight: 500 }}>
                                Jumlah ({unitLabel}) {hasDateTime && <span style={{ fontSize: '11px', color: '#94a3b8' }}>(Maks {maxCap})</span>}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                  type="button"
                                  disabled={addonPaxCount <= 1}
                                  onClick={(e) => { e.stopPropagation(); setAddonPax(addon.id, addonPaxCount - 1); }}
                                  style={{ 
                                    width: '26px', 
                                    height: '26px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #d4d4d8', 
                                    background: '#fff', 
                                    cursor: addonPaxCount <= 1 ? 'not-allowed' : 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: '13px', 
                                    fontWeight: 600, 
                                    color: '#52525b', 
                                    transition: 'all 0.15s ease', 
                                    opacity: addonPaxCount <= 1 ? 0.4 : 1 
                                  }}
                                >
                                  −
                                </button>
                                <span style={{ fontFamily: '"Outfit", sans-serif', fontSize: '13px', fontWeight: 700, color: '#18181b', minWidth: '20px', textAlign: 'center' }}>
                                  {addonPaxCount}
                                </span>
                                <button
                                  type="button"
                                  disabled={addonPaxCount >= maxCap}
                                  onClick={(e) => { e.stopPropagation(); setAddonPax(addon.id, addonPaxCount + 1); }}
                                  style={{ 
                                    width: '26px', 
                                    height: '26px', 
                                    borderRadius: '6px', 
                                    border: '1px solid #d4d4d8', 
                                    background: '#fff', 
                                    cursor: addonPaxCount >= maxCap ? 'not-allowed' : 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: '13px', 
                                    fontWeight: 600, 
                                    color: '#52525b', 
                                    transition: 'all 0.15s ease', 
                                    opacity: addonPaxCount >= maxCap ? 0.4 : 1 
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {discount > 0 && (
                <div className="experience-discount">
                  <div className="discount-label">
                    <span>Promo Applied ({appliedPromo?.code})</span>
                  </div>
                  <span className="discount-value">- IDR {discount.toLocaleString()}</span>
                </div>
              )}

              <div className="experience-total">
                <span className="total-label">Total Amount</span>
                <span className="total-amount">IDR {totalPrice.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Right Side - Booking Form */}
          <div className="booking-form-container animate-on-scroll">
            <div className="form-header">
              <h2>Guest Information</h2>
              <p>Please fill in your details to complete the booking</p>
            </div>

            <form className="booking-form" onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="form-group">
                <label htmlFor="name">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={form.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              {/* Phone */}
              <div className="form-group">
                <label htmlFor="phone">
                  WhatsApp Number <span className="required">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  placeholder="Example: +628123456789 or +6591234567"
                  value={form.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              {/* Number of Guests */}
              <div className="form-group">
                <label htmlFor="pax">
                  Number of Guests <span className="required">*</span>
                </label>
                <div className="pax-selector">
                  <button
                    type="button"
                    className="pax-btn"
                    onClick={() => {
                      if (experience?.id === 'prewedding') return;
                      const minLimit = fullExp?.minPax || 1;
                      setForm({ ...form, pax: Math.max(minLimit, form.pax - 1) });
                    }}
                  >
                    −
                  </button>
                  <input
                    id="pax"
                    type="number"
                    name="pax"
                    min={experience?.id === 'prewedding' ? 1 : (fullExp?.minPax || 1)}
                    max={experience?.id === 'prewedding' ? 1 : getRemainingCapacity(form.date, form.time)}
                    value={form.pax}
                    onChange={handleChange}
                    className={errors.pax ? 'error' : ''}
                    readOnly
                  />
                  <button
                    type="button"
                    className="pax-btn"
                    onClick={() => {
                      if (experience?.id === 'prewedding') return;
                      const maxLimit = getRemainingCapacity(form.date, form.time);
                      setForm({ ...form, pax: Math.min(maxLimit, form.pax + 1) });
                    }}
                  >
                    +
                  </button>
                </div>
                {fullExp?.minPax || fullExp?.defaultCapacity ? (
                  <p className="pax-helper-text">
                    {fullExp.minPax ? `Min. ${fullExp.minPax} ` : ''}
                    {fullExp.minPax && getRemainingCapacity(form.date, form.time) ? '· ' : ''}
                    {getRemainingCapacity(form.date, form.time) ? `Max. ${experience?.id === 'prewedding' ? 10 : getRemainingCapacity(form.date, form.time)} ` : ''}
                    person{(getRemainingCapacity(form.date, form.time) > 1 || fullExp.minPax > 1) ? 's' : ''} per booking
                  </p>
                ) : null}
                {errors.pax && <span className="error-message">{errors.pax}</span>}
              </div>

              {/* Promo Code Field */}
              <div className="promo-input-group">
                <div className="promo-label-wrapper">
                  <label htmlFor="promoCode">Kode Promo</label>
                </div>
                <div className="promo-field-container">
                  <span className="promo-icon">🎟</span>
                  <input
                    id="promoCode"
                    name="promoCode"
                    type="text"
                    placeholder="Masukkan kode promo"
                    value={form.promoCode}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="promo-apply-btn"
                    onClick={handleApplyPromo}
                  >
                    Apply
                  </button>
                </div>
                {appliedPromo && (
                  <div className="promo-success-msg">
                    <span>✓</span>
                    <span>{appliedPromo.title} Applied</span>
                  </div>
                )}
              </div>

              {/* Drone Add-on for Bicycle */}
              {experience?.id === 'bicycle' && (
                <div className="form-group addon-group" style={{
                  background: 'rgba(201, 169, 110, 0.05)',
                  padding: '15px',
                  borderRadius: '12px',
                  border: '1px solid rgba(201, 169, 110, 0.2)',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      id="withDrone"
                      type="checkbox"
                      name="withDrone"
                      checked={form.withDrone}
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <label htmlFor="withDrone" style={{ margin: 0, cursor: 'pointer', fontWeight: '600' }}>
                      Add Drone Video & Photo Content (+IDR 300,000)
                    </label>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginLeft: '32px' }}>
                    Capture your cycling journey with professional aerial footage and professional editing.
                  </p>
                </div>
              )}

              {/* Preferred Date Calendar */}
              <div className="form-group">
                <label>
                  Preferred Date <span className="required">*</span>
                </label>

                <div className="booking-calendar">
                  <div className="booking-calendar-header">
                    <div>
                      <p>{monthLabel.toUpperCase()}</p>
                    </div>
                    <div className="calendar-nav">
                      <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month">&#8249;</button>
                      <button type="button" onClick={() => goMonth(1)} aria-label="Next month">&#8250;</button>
                    </div>
                  </div>

                  <div className="calendar-weekdays">
                    {weekdayLabels.map((day) => (
                      <span key={day}>{day}</span>
                    ))}
                  </div>

                  <div className="calendar-grid">
                    {calendarCells.map((dateObj, idx) => {
                      if (!dateObj) {
                        return <span key={`blank-${idx}`} className="calendar-day-empty"></span>;
                      }

const key = formatDateKey(dateObj);
                      const paxBooked = bookedPaxByDate[key] || 0;
                      
                      const isBeforeToday = dateObj < today;
                      const isToday = dateObj.getTime() === today.getTime();
                      
                      // For mantos: check if date is outside July 1-5, 2026
                      if (isMantosExperience) {
                        const year = dateObj.getFullYear();
                        const month = dateObj.getMonth();
                        const day = dateObj.getDate();
                        const isJuly2026 = year === 2026 && month === 6;
                        const isInRange = day >= 1 && day <= 5;
                        if (!isJuly2026 || !isInRange) {
                          return (
                            <button
                              key={key}
                              type="button"
                              className="calendar-day disabled"
                              disabled={true}
                            >
                              <span className="day-number">{dateObj.getDate()}</span>
                              <span className="day-note">CLOSED</span>
                            </button>
                          );
                        }
                      }
                      
                      const block = blockedDates.find(b => b.date === key);
                      
                      // Default: Izinkan booking hari ini (Hari H), batasi hanya tanggal sebelum hari ini
                      const isPast = isBeforeToday;
                      const isBlocked = block && block.isClosed;

                      const limit = getBookingLimit(key);

                      // Cek status hari berdasarkan ketersediaan slot
                      const allSlots = timeSlotSections.flatMap(s => s.slots);
                      let availableSlotsCount = 0;
                      let totalSlotsCount = 0;

allSlots.forEach(slot => {
                         const slotKey = `${key}|${slot}`;
                         const booked = bookedPaxByDateTime[slotKey] || 0;
                         const block = blockedDates.find(b => b.date === key);
                         const isSlotBlocked = block && block.slots && block.slots.includes(slot);
                         const isSessionBased = sessionBasedExperiences.includes(experience?.id);
                         // For mantos: capacity is 6 pax per session
                         const capacity = isMantosExperience
                           ? (fullExp?.defaultCapacity || 6)
                           : (isSessionBased
                               ? (maxSessionsMap[experience?.id] || 1)
                               : (fullExp?.defaultCapacity || 6));

                         if (!isSlotBlocked) {
                           totalSlotsCount++;
                           if (booked < capacity) {
                             availableSlotsCount++;
                           }
                         }
                       });

                      const isFull = (totalSlotsCount > 0 && availableSlotsCount === 0) || isBlocked;
                      const isLimited = !isFull && availableSlotsCount <= 2 && availableSlotsCount > 0;
                      const isSelected = form.date === key;

                      let statusClass = "available";
                      if (isPast) statusClass = "disabled";
                      else if (isFull) statusClass = "full";
                      else if (isLimited) statusClass = "limited";

                      return (
                        <button
                          key={key}
                          type="button"
                          className={`calendar-day ${statusClass} ${isSelected ? "selected" : ""}`.trim()}
                          onClick={() => handleDateSelect(dateObj)}
                          disabled={isPast || isFull}
                        >
                          <span className="day-number">{dateObj.getDate()}</span>
                          {isFull && <span className="day-note">{isBlocked ? "CLOSED" : "FULL"}</span>}
                          {isLimited && <span className="day-note">LIMITED</span>}
                        </button>
                      );
                    })}
                  </div>

                  <div className="calendar-legend">
                    <span><i className="dot available"></i>Available</span>
                    <span><i className="dot limited"></i>Limited</span>
                    <span><i className="dot full"></i>Full</span>
                  </div>

                  {form.date && (
                    <p className="calendar-selected">Selected: {form.date}</p>
                  )}

                  <div className="time-slot-picker">
                    <h4>Select Time</h4>

                    {!form.date && (
                      <p className="time-slot-helper">Select a date first to see available times.</p>
                    )}

                    {form.date && timeSlotSections.map((section) => {
                      const visibleSlots = section.slots.filter(slot => {
                        const fullExp = getExp(experience?.id);
                        const block = blockedDates.find(b => b.date === form.date);
                        
                        // Mantos experiences have fixed hours 10:00-20:00
                        const mantosHours = experience?.id?.startsWith('mantos-');
                        const open1 = mantosHours ? "10:00" : ((block && block.openTime) || fullExp?.openTime || "08:00");
                        const close1 = mantosHours ? "20:00" : ((block && block.closeTime) || fullExp?.closeTime || "20:00");
                        const open2 = (block && block.openTime2) || fullExp?.openTime2;
                        const close2 = (block && block.closeTime2) || fullExp?.closeTime2;
                        
                        let inRange = false;
                        if (open1 && close1 && slot >= open1 && slot <= close1) inRange = true;
                        if (open2 && close2 && slot >= open2 && slot <= close2) inRange = true;
                        return inRange;
                      });
                      if (visibleSlots.length === 0) return null;
                      return (
                        <div className="time-slot-section" key={section.label}>
                          <div className="time-slot-section-title">{section.label}</div>
                          <div className="time-slot-grid">
                            {visibleSlots.map((slot) => {
                              const isTodaySelected = form.date === formatDateKey(today);
                               const now = new Date(new Date().toLocaleString("en-US", {timeZone: "Asia/Makassar"}));
                              const currentMinutes = (now.getHours() * 60) + now.getMinutes();
                              const isPastTime = isTodaySelected && timeToMinutes(slot) <= currentMinutes;

                              const block = blockedDates.find(b => b.date === form.date);
                              const isSlotBlockedByAdmin = block && block.slots && block.slots.includes(slot);

                              // Check if this slot is an actual booking start time
                              const slotKey = `${form.date}|${slot}`;
                              const isStartTime = bookedStartTimes[slotKey] === true;
                              const slotDirectPax = bookedPaxByDateTime[slotKey] || 0;

                              // Use getRemainingCapacity for forward-looking check across full duration
                              const remaining = getRemainingCapacity(form.date, slot);

                              const singleGroupExperiences = ['sailing', 'prewedding'];
                              const isSessionBased = sessionBasedExperiences.includes(experience?.id);
                              const isSingleGroup = singleGroupExperiences.includes(experience?.id);
                              const isMantosExp = experience?.id?.startsWith('mantos-');
                              let capacity = isSingleGroup
                                ? (fullExp?.defaultCapacity || 15)
                                : isSessionBased
                                  ? (maxSessionsMap[experience?.id] || 1)
                                  : isMantosExp
                                    ? (fullExp?.defaultCapacity || 6)
                                    : (fullExp?.defaultCapacity || 6);
                              if (block && block.slots) {
                                const slotOverride = block.slots.find(s => s.time === slot);
                                if (slotOverride && typeof slotOverride.maxPax === 'number') {
                                  capacity = slotOverride.maxPax;
                                }
                              }
                              const occupied = capacity - remaining;
                              const isSelected = form.time === slot;

                              // Slot is mid-session: occupied by a booking's duration but NOT a start time
                              const isMidSession = slotDirectPax > 0 && !isStartTime;

                              // Slot has overlap issues: starting here would overlap with occupied slots
                              const hasOverlapIssue = occupied > 0 && !isStartTime;

                              // Slot is full: no remaining capacity even at the start time
                              const isFull = remaining <= 0;

                              // Determine if slot should be disabled
                              // For Workshops: must join existing start times or pick a completely free slot
                              // For others: just check capacity
                              const isDisabled = isPastTime || isSlotBlockedByAdmin || isFull || (isWorkshopExperience && (isMidSession || hasOverlapIssue));

                              let stateLabel = "AVAILABLE";
                              let stateClass = "available";

                              if (isPastTime) {
                                stateLabel = "PAST";
                                stateClass = "disabled";
                              } else if (isSlotBlockedByAdmin) {
                                stateLabel = "CLOSED";
                                stateClass = "full";
                              } else if (isFull) {
                                stateLabel = "FULL";
                                stateClass = "full";
                              } else if (isWorkshopExperience && (isMidSession || hasOverlapIssue)) {
                                stateLabel = "OCCUPIED";
                                stateClass = "full";
                              } else if (isSelected) {
                                stateLabel = `SELECTED · ${remaining}/${capacity} Left`;
                                stateClass = "selected";
                              } else if ((isWorkshopExperience ? isStartTime : occupied > 0) && remaining <= 2) {
                                const unit = isSessionBased ? (sessionUnitMap[experience?.id] || "Slot") : "Pax";
                                stateLabel = `${remaining}/${capacity} ${unit} Left`;
                                stateClass = "limited";
                              } else if (isWorkshopExperience ? isStartTime : occupied > 0) {
                                const unit = isSessionBased ? (sessionUnitMap[experience?.id] || "Slot") : "Pax";
                                stateLabel = `${remaining}/${capacity} ${unit} Left`;
                                stateClass = "available";
                              }

                              return (
                                <button
                                  type="button"
                                  key={slot}
                                  className={`time-slot-card ${stateClass}`.trim()}
                                  disabled={isDisabled}
                                  onClick={() => handleTimeSelect(slot)}
                                  style={{
                                    height: 'auto',
                                    minHeight: '76px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'stretch',
                                    padding: '10px 12px'
                                  }}
                                >
                                  <div style={{ textAlign: 'left', width: '100%' }}>
                                    <span className="slot-time" style={{ display: 'block', lineHeight: '1.2' }}>{slot}</span>
                                    <span className="slot-state" style={{ display: 'block', marginTop: '2px', lineHeight: '1.2' }}>{stateLabel}</span>
                                  </div>
                                  
                                  {Object.keys(selectedAddons).length > 0 && (
                                    <div style={{
                                      marginTop: '6px',
                                      paddingTop: '4px',
                                      borderTop: '1px dashed rgba(139, 115, 85, 0.15)',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '2px',
                                      width: '100%',
                                      textAlign: 'left'
                                    }}>
                                      {Object.keys(selectedAddons).map(addonId => {
                                        const addon = experiences.find(e => e.id === addonId);
                                        if (!addon) return null;
                                        const addonCap = getRemainingCapacity(form.date, slot, addonId);
                                        return (
                                          <div 
                                            key={addonId} 
                                            style={{
                                              fontSize: '9px',
                                              fontWeight: '700',
                                              color: addonCap <= 0 ? '#dc2626' : isSelected ? '#7d6445' : '#64748b',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '3px',
                                              lineHeight: '1'
                                            }}
                                          >
                                            <span>{addon.emoji}</span>
                                            <span>{addonCap} Left</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {/* Selected Session Capacity Detail */}
                    {form.date && form.time && (
                      <div className="session-capacity-detail" style={{
                        marginTop: '20px',
                        padding: '16px 20px',
                        background: '#faf9f6',
                        borderRadius: '12px',
                        border: '1.5px dashed rgba(139, 115, 85, 0.3)',
                        fontFamily: '"Outfit", sans-serif'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '14px',
                          borderBottom: '1px solid rgba(139, 115, 85, 0.15)',
                          paddingBottom: '8px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            color: '#8B7355'
                          }}>
                            Detail Ketersediaan Sesi · {form.time}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {/* Primary Experience */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                                  {experience?.name}
                                </span>
                                <span style={{ fontSize: '10px', color: '#8B7355', fontWeight: 500 }}>
                                  (Experience Utama)
                                </span>
                              </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color: getRemainingCapacity(form.date, form.time) <= 2 ? '#d97706' : '#1e293b'
                                }}>
                                {getRemainingCapacity(form.date, form.time)} / {(() => {
                                  const primaryCustomRule = allAvailabilityRules.find(r => r.experienceId === experience?.id && r.date === form.date);
                                  const primarySlotOverride = primaryCustomRule?.slots?.find(s => s.time === form.time);
                                  if (primarySlotOverride && typeof primarySlotOverride.maxPax === 'number') {
                                    return primarySlotOverride.maxPax;
                                  }
                                  return ['sailing', 'prewedding'].includes(experience?.id) 
                                    ? (fullExp?.defaultCapacity || 15) 
                                    : sessionBasedExperiences.includes(experience?.id) 
                                      ? (maxSessionsMap[experience?.id] || 1) 
                                      : (fullExp?.defaultCapacity || 6);
                                })()}
                              </span>
                              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                {['sailing', 'prewedding'].includes(experience?.id) ? "Pax" : sessionBasedExperiences.includes(experience?.id) ? (sessionUnitMap[experience?.id] || "Slot") : "Pax"} Tersedia
                              </span>
                            </div>
                          </div>

                          {/* Selected Addons */}
                          {Object.keys(selectedAddons).map(addonId => {
                            const addon = experiences.find(e => e.id === addonId);
                            if (!addon) return null;
                            const addonCap = getRemainingCapacity(form.date, form.time, addonId);
                            const isAddonSingleGroup = ['sailing', 'prewedding'].includes(addonId);
                            const addonTotalCap = (() => {
                              const customAddonRule = allAvailabilityRules.find(r => r.experienceId === addonId && r.date === form.date);
                              const addonSlotOverride = customAddonRule?.slots?.find(s => s.time === form.time);
                              if (addonSlotOverride && typeof addonSlotOverride.maxPax === 'number') {
                                return addonSlotOverride.maxPax;
                              }
                              return isAddonSingleGroup
                                ? (addon.defaultCapacity || 15)
                                : sessionBasedExperiences.includes(addonId)
                                  ? (maxSessionsMap[addonId] || 1)
                                  : (addon.defaultCapacity || 6);
                            })();
                            const unit = isAddonSingleGroup
                              ? "Pax"
                              : sessionBasedExperiences.includes(addonId)
                                ? (sessionUnitMap[addonId] || "Slot")
                                : "Pax";
                            
                            return (
                              <div key={addonId} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                paddingTop: '8px',
                                borderTop: '1px solid #f1f0ea'
                              }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                                      {addon.name}
                                    </span>
                                    <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 500 }}>
                                      (Tambahan / Add-on)
                                    </span>
                                  </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: addonCap <= 0 ? '#ef4444' : addonCap <= 2 ? '#d97706' : '#1e293b'
                                  }}>
                                    {addonCap} / {addonTotalCap}
                                  </span>
                                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                                    {unit === "Pax" ? "Pax" : unit === "Boat" ? "Perahu" : unit === "Trip" ? "Trip" : "Slot"} Tersedia
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {errors.date && <span className="error-message">{errors.date}</span>}
                {errors.time && <span className="error-message">{errors.time}</span>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="booking-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <span>Continue to Payment</span>
                    <span className="button-arrow">→</span>
                  </>
                )}
              </button>

              {/* Cancel Button */}
              <button
                type="button"
                className="booking-cancel-btn"
                onClick={() => navigate(-1)}
              >
                Cancel Booking
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {/* Modern Notification Modal */}
      {notification.show && (
        <div className="promo-modal-overlay" onClick={() => setNotification({ ...notification, show: false })}>
          <div className="promo-modal-content" onClick={(e) => e.stopPropagation()}>
            <div
              className={`modal-icon-wrapper ${notification.type}`}
              onClick={() => setNotification({ ...notification, show: false })}
            >
              {notification.type === 'success' ? '✓' : '✕'}
            </div>
            <h3 className="modal-title">{notification.title}</h3>
            <p className="modal-message">{notification.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}